import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Layout from "../../components/layout";
import { route } from "../../static/route";
import { POST } from "../../services/axios.service";
import { mergePdfs } from "../../services/pdf.service";
import { IDeliverFormInput, OrderProps } from "../../types/order";
import styles from "./DeliverOrdersPage.module.scss";

const DeliverOrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { orders: OrderProps[] } | null;
  const passedOrders = state?.orders || [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMergingPdfs, setIsMergingPdfs] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IDeliverFormInput>({
    defaultValues: {
      orders: passedOrders.map((order) => ({
        order_id: order.id,
        qty_field_1: undefined as any,
        weight_field_1: undefined as any,
        qty_field_2: undefined as any,
        weight_field_2: undefined as any,
        close_order: false,
      })),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "orders",
  });

  const onSubmit = async (data: IDeliverFormInput) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setProgress({ current: 0, total: data.orders.length });

    const collectedPdfUrls: string[] = [];
    const collectedTransactionIds: number[] = [];

    for (let i = 0; i < data.orders.length; i++) {
      setProgress({ current: i + 1, total: data.orders.length });
      const entry = data.orders[i];
      const order = passedOrders[i];
      const isInSet = order?.item?.isinset || false;

      const requestObj: any = {
        order_id: entry.order_id,
        qty_field_1: entry.qty_field_1 || 0,
        weight_field_1: entry.weight_field_1 || 0,
        close_order: entry.close_order || false,
      };

      if (isInSet) {
        requestObj.qty_field_2 = entry.qty_field_2 || 0;
        requestObj.weight_field_2 = entry.weight_field_2 || 0;
      }

      try {
        const response: any = await POST(
          "/v1/worker/receive-transactions",
          requestObj
        );

        if (response.data.status === "success") {
          if (response.data.data?.pdf_url) {
            collectedPdfUrls.push(response.data.data.pdf_url);
            if (response.data.data?.transaction?.id) {
              collectedTransactionIds.push(response.data.data.transaction.id);
            }
          }
        } else {
          setSubmitError(
            `ઓર્ડર #${entry.order_id}: ${response.data.message || "કંઈક ખોટું ગયું"}`
          );
          setIsSubmitting(false);
          return;
        }
      } catch (error: any) {
        setSubmitError(
          `ઓર્ડર #${entry.order_id}: ${error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો."}`
        );
        setIsSubmitting(false);
        return;
      }
    }

    // Open collected PDFs
    if (collectedPdfUrls.length === 1) {
      window.open(collectedPdfUrls[0], "_blank");
    } else if (collectedTransactionIds.length > 1) {
      try {
        setIsMergingPdfs(true);
        const mergedBlobUrl = await mergePdfs(collectedTransactionIds);
        window.open(mergedBlobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(mergedBlobUrl), 30000);
      } catch {
        // Fallback: open each PDF individually
        collectedPdfUrls.forEach((url) => window.open(url, "_blank"));
      } finally {
        setIsMergingPdfs(false);
      }
    }

    setIsSubmitting(false);
    navigate(route.openOrder);
  };

  if (passedOrders.length === 0) {
    return (
      <Layout>
        <div className={styles.DeliverOrdersPage}>
          <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
            <FaArrowLeft
              onClick={() => navigate(route.openOrder)}
              className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
            />
            <h1 className="text-4xl mt-6 font-bold text-white">
              ઈન્વૉઇસ ડિલિવરી
            </h1>
          </div>
          <div className="w-full relative mt-[70px] px-10 py-5 text-center">
            <p className="mt-4 text-primary-2 font-bold text-2xl">
              કોઈ ઓર્ડર પસંદ કરવામાં આવ્યા નથી.
            </p>
            <button
              onClick={() => navigate(route.openOrder)}
              className="mx-auto mt-8 shadow rounded-3xl text-2xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
            >
              પાછા જાઓ
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.DeliverOrdersPage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={() => navigate(route.openOrder)}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />
          <h1 className="text-4xl mt-6 font-bold text-white">
            ઈન્વૉઇસ ડિલિવરી
          </h1>
          <p className="text-white text-lg mt-2">
            {passedOrders.length} ઓર્ડર પસંદ કરેલ
          </p>
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5">
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-4 text-center">
              {submitError}
            </div>
          )}

          {isSubmitting && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl mb-4 text-center font-bold text-lg">
              {isMergingPdfs
                ? "PDF તૈયાર થઈ રહી છે..."
                : `ડિલિવરી સેવ થઈ રહી છે... (${progress.current}/${progress.total})`}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((field, index) => {
              const order = passedOrders[index];
              const isInSet = order?.item?.isinset || false;

              return (
                <div
                  key={field.id}
                  className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6"
                >
                  {/* Order Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-[70px] h-[70px] rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100 flex-shrink-0">
                      {order.image ? (
                        <img
                          className="w-full h-full object-cover"
                          src={`${order.image}?order=${order.id}`}
                          alt=""
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <span className="text-gray-400 text-xs">
                            No Image
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 text-sm">
                          Order No:
                        </span>
                        <span className="text-primary-1 text-lg font-bold">
                          {order.id}
                        </span>
                        {isInSet && (
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                            સેટ
                          </span>
                        )}
                      </div>
                      <p className="text-primary-1 font-bold text-base">
                        {order.party?.firm_name || "-"}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {order.item?.name || "-"}
                      </p>
                    </div>
                  </div>

                  {/* Existing delivery totals */}
                  {isInSet ? (
                    <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">ઓર્ડર નંગ:</span>
                        <span className="text-primary-1 font-bold">
                          {order.qty || 0}
                        </span>
                      </div>
                      {((order.set_delivered_qty || 0) > 0 ||
                        (order.set_delivered_weight || 0) > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">કેશ:</span>
                          <span className="text-primary-2 font-bold">
                            {order.set_delivered_qty || 0} નંગ |{" "}
                            {(order.set_delivered_weight || 0).toFixed(3)} કિ.
                          </span>
                        </div>
                      )}
                      {((order.delivered_qty || 0) > 0 ||
                        (order.delivered_weight || 0) > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">પટ્ટા:</span>
                          <span className="text-green-600 font-bold">
                            {order.delivered_qty || 0} નંગ |{" "}
                            {(order.delivered_weight || 0).toFixed(3)} કિ.
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">ઓર્ડર નંગ:</span>
                        <span className="text-primary-1 font-bold">
                          {order.qty || 0}
                        </span>
                      </div>
                      {((order.delivered_qty || 0) > 0 ||
                        (order.delivered_weight || 0) > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">પરત:</span>
                          <span className="text-green-600 font-bold">
                            {order.delivered_qty || 0} નંગ |{" "}
                            {(order.delivered_weight || 0).toFixed(3)} કિ.
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hidden order_id */}
                  <input
                    type="hidden"
                    {...register(`orders.${index}.order_id` as const)}
                  />

                  {/* Form Fields */}
                  {isInSet && (
                    <>
                      {/* Kesh section */}
                      <div className="bg-gray-50 rounded-xl p-4 mb-3">
                        <h4 className="text-base font-bold text-primary-1 mb-3 text-center">
                          કેશ{" "}
                          <span className="text-gray-400 font-normal text-sm">
                            (વૈકલ્પિક)
                          </span>
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="text-gray-600 font-medium text-sm mb-1 block">
                              ડિલિવરી નંગ
                            </label>
                            <input
                              className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2 focus:border-primary-1"
                              type="number"
                              step="1"
                              min="0"
                              pattern="[0-9]*"
                              inputMode="numeric"
                              placeholder="કેશ નંગ દાખલ કરો"
                              onFocus={(e) =>
                                e.target.addEventListener(
                                  "wheel",
                                  function (e) {
                                    e.preventDefault();
                                  },
                                  { passive: false }
                                )
                              }
                              {...register(
                                `orders.${index}.qty_field_2` as const,
                                {
                                  min: 0,
                                  valueAsNumber: true,
                                }
                              )}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600 font-medium text-sm mb-1 block">
                              ડિલિવરી વજન (કિલો)
                            </label>
                            <input
                              className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2 focus:border-primary-1"
                              type="number"
                              step="0.0001"
                              min="0"
                              inputMode="decimal"
                              placeholder="કેશ વજન દાખલ કરો"
                              onFocus={(e) =>
                                e.target.addEventListener(
                                  "wheel",
                                  function (e) {
                                    e.preventDefault();
                                  },
                                  { passive: false }
                                )
                              }
                              {...register(
                                `orders.${index}.weight_field_2` as const,
                                {
                                  min: 0,
                                  valueAsNumber: true,
                                }
                              )}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Patta section */}
                      <div className="bg-gray-50 rounded-xl p-4 mb-3">
                        <h4 className="text-base font-bold text-primary-1 mb-3 text-center">
                          પટ્ટા
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="text-gray-600 font-medium text-sm mb-1 block">
                              ડિલિવરી નંગ
                            </label>
                            <input
                              className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                              type="number"
                              step="1"
                              min="0"
                              pattern="[0-9]*"
                              inputMode="numeric"
                              placeholder="પટ્ટા નંગ દાખલ કરો"
                              onFocus={(e) =>
                                e.target.addEventListener(
                                  "wheel",
                                  function (e) {
                                    e.preventDefault();
                                  },
                                  { passive: false }
                                )
                              }
                              {...register(
                                `orders.${index}.qty_field_1` as const,
                                {
                                  required: true,
                                  min: 0,
                                  valueAsNumber: true,
                                }
                              )}
                            />
                            {errors?.orders?.[index]?.qty_field_1 && (
                              <span className="text-red-500 text-sm">
                                ડિલિવરી નંગ દાખલ કરો
                              </span>
                            )}
                          </div>
                          <div>
                            <label className="text-gray-600 font-medium text-sm mb-1 block">
                              ડિલિવરી વજન (કિલો)
                            </label>
                            <input
                              className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                              type="number"
                              step="0.0001"
                              min="0"
                              inputMode="decimal"
                              placeholder="પટ્ટા વજન દાખલ કરો"
                              onFocus={(e) =>
                                e.target.addEventListener(
                                  "wheel",
                                  function (e) {
                                    e.preventDefault();
                                  },
                                  { passive: false }
                                )
                              }
                              {...register(
                                `orders.${index}.weight_field_1` as const,
                                {
                                  required: true,
                                  min: 0,
                                  valueAsNumber: true,
                                }
                              )}
                            />
                            {errors?.orders?.[index]?.weight_field_1 && (
                              <span className="text-red-500 text-sm">
                                ડિલિવરી વજન દાખલ કરો
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {!isInSet && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-3">
                      <div className="space-y-3">
                        <div>
                          <label className="text-gray-600 font-medium text-sm mb-1 block">
                            ડિલિવરી નંગ
                          </label>
                          <input
                            className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                            type="number"
                            step="1"
                            min="0"
                            pattern="[0-9]*"
                            inputMode="numeric"
                            placeholder="ડિલિવરી નંગ દાખલ કરો"
                            onFocus={(e) =>
                              e.target.addEventListener(
                                "wheel",
                                function (e) {
                                  e.preventDefault();
                                },
                                { passive: false }
                              )
                            }
                            {...register(
                              `orders.${index}.qty_field_1` as const,
                              {
                                required: true,
                                min: 0,
                                valueAsNumber: true,
                              }
                            )}
                          />
                          {errors?.orders?.[index]?.qty_field_1 && (
                            <span className="text-red-500 text-sm">
                              ડિલિવરી નંગ દાખલ કરો
                            </span>
                          )}
                        </div>
                        <div>
                          <label className="text-gray-600 font-medium text-sm mb-1 block">
                            ડિલિવરી વજન (કિલો)
                          </label>
                          <input
                            className="w-full h-12 px-4 text-primary-1 font-semibold text-lg border-2 border-primary-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-2"
                            type="number"
                            step="0.0001"
                            min="0"
                            inputMode="decimal"
                            placeholder="ડિલિવરી વજન દાખલ કરો"
                            onFocus={(e) =>
                              e.target.addEventListener(
                                "wheel",
                                function (e) {
                                  e.preventDefault();
                                },
                                { passive: false }
                              )
                            }
                            {...register(
                              `orders.${index}.weight_field_1` as const,
                              {
                                required: true,
                                min: 0,
                                valueAsNumber: true,
                              }
                            )}
                          />
                          {errors?.orders?.[index]?.weight_field_1 && (
                            <span className="text-red-500 text-sm">
                              ડિલિવરી વજન દાખલ કરો
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Close Order Checkbox */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-5 h-5 accent-primary-1 rounded"
                        {...register(
                          `orders.${index}.close_order` as const
                        )}
                      />
                      <div>
                        <span className="text-primary-1 font-semibold text-sm">
                          ઓર્ડર પૂર્ણ કરો
                        </span>
                        <p className="text-xs text-gray-500">
                          ફક્ત ત્યારે જ ટિક કરો જ્યારે આ છેલ્લી ડિલિવરી હોય
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              );
            })}

            {/* Submit Button */}
            <button
              className="w-full py-4 rounded-xl text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold shadow-lg disabled:opacity-50 mb-10"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isMergingPdfs
                  ? "PDF તૈયાર થઈ રહી છે..."
                  : `ડિલિવરી સેવ થઈ રહી છે... (${progress.current}/${progress.total})`
                : "સેવ કરો અને રસીદ જુઓ"}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default DeliverOrdersPage;
